import { createSelector } from "@reduxjs/toolkit";
import { UtilsTypes } from "@stellar/utils";
import { projectsAdapter } from "@store/projects/projects-slice";
import { hasUserValidPermissionProjectLevel } from "@utils/permission-control/project-permission-control";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { getProjectArchivingState, getProjectManager, getProjectManagerName, isFeatureEnabled, isProjectDemo } from "@utils/project-utils";
import { isMemberMatchingSearch } from "@store/members/members-selector";

/** The project list state plus the resolved projects array */

/**
 * Returns all projects
 */
export const projectsSelector = createSelector(state => state.projects, projectsState => {
  return projectsAdapter.getSelectors().selectAll(projectsState);
});

/**
 * Returns all projects mapped by their id
 */
export const projectsMapByIdSelector = createSelector(state => state.projects.entities, projects => {
  return new Map(Object.entries(projects));
});

/**
 * Returns the ids of all projects
 */
export const projectIdsSelector = createSelector(state => state.projects, projectsState => {
  return projectsState.ids;
});

/**
 * Returns true if a given project can be archived.
 *
 * @param project
 */
export function isProjectArchivingAllowedSelector(project) {
  const getIsProjectAllowedToBeArchived = isFeatureEnabledForProjectSelector(project.id, "project-archive");
  return createSelector(isProjectActiveSelector(project.id), getIsProjectAllowedToBeArchived, (isActive, isAllowed) => {
    return isActive && isAllowed;
  });
}

/**
 * Returns true if a given project can be unarchived.
 *
 * @param project
 */
export function isProjectUnarchivingAllowedSelector(project) {
  const getIsProjectAllowedToBeUnarchived = isFeatureEnabledForProjectSelector(project.id, "project-unarchive");
  return createSelector(projectArchivingStateSelector(project.id), getIsProjectAllowedToBeUnarchived, (archivingState, isAllowed) => {
    return archivingState === UtilsTypes.ProjectArchivingState.archived && isAllowed && project.archivingState !== "ARCHIVED_DOWNLOADED";
  });
}

/**
 * Returns the fetching properties of the projects slice.
 */
export const fetchingProjectsFlagsSelector = createSelector(state => state.projects, projectsState => projectsState.fetching);

/**
 * Returns the deleted projects map of the projects slice
 */
export const deletedProjectsSelector = createSelector(state => state.projects, projectsState => projectsState.deletedProjects);

/**
 * Returns how many projects the transient list holds in total, which is the list that the group
 * scoped and the searched or sorted project tables are fetched into.
 */
export const transientProjectsTotalCountSelector = createSelector(state => state.projects.lists.transient, list => list.totalCount);

/**
 * Returns true if a given a project is currently ongoing a process.
 *
 * @param projectId
 */
export function isProjectProcessingSelector(projectId) {
  return createSelector(state => state.projects.fetching.processingProjects, processingProjects => {
    return processingProjects[projectId];
  });
}

/**
 * Get project details by providing the projectId
 */
export function getProjectByIdSelector(projectId) {
  return createSelector(state => state.projects, projects => {
    if (!projectId) {
      return null;
    }
    return projectsAdapter.getSelectors().selectById(projects, projectId) ?? null;
  });
}

/**
 * Get the selected project using the selected project Id and finding it on the store,
 * returns null if there's no projectId or if the project could not be found in the store.
 */
export const selectedProjectSelector = createSelector(state => state.projects.selectedProject.id, state => state.projects, (selectedProjectId, projects) => {
  if (!selectedProjectId) {
    return null;
  }
  return projectsAdapter.getSelectors().selectById(projects, selectedProjectId) ?? null;
});

/**
 * Get the selected project ID
 */
export const selectedProjectIdSelector = createSelector(state => state.projects.selectedProject, selectedProject => {
  return selectedProject.id;
});

/**
 * Returns the snapshots of the selected project from the project entity
 */
export const selectedProjectSnapshotsSelector = createSelector(selectedProjectSelector, project => {
  return project?.snapshots ?? [];
});

/**
 * Returns whether the snapshots of the selected project are currently being fetched
 */
export const isFetchingProjectSnapshotsSelector = createSelector(state => state.projects, projectsState => projectsState.fetching.isFetchingProjectSnapshots);

/**
 * Returns the number of snapshots that are currently being created for the selected project
 */
export const snapshotsBeingCreatedCountSelector = createSelector(state => state.projects, projectsState => projectsState.selectedProject.snapshotsBeingCreatedCount);

/**
 * Returns the members of the currently selected snapshot
 */
export const selectedSnapshotMembersSelector = createSelector(state => state.projects, projectsState => projectsState.selectedProject.selectedSnapshotMembers);

/**
 * Returns the snapshot selected to display in the side panel
 */
export const selectedSnapshotSelector = createSelector(state => state.projects.selectedProject.selectedSnapshotId, selectedProjectSelector, (selectedSnapshotId, project) => {
  if (!selectedSnapshotId) {
    return undefined;
  }
  return project?.snapshots.find(snapshot => snapshot.id === selectedSnapshotId);
});

/**
 * Returns whether the snapshot side panel is open
 */
export const isSnapshotSidePanelOpenSelector = createSelector(state => state.projects, projectsState => projectsState.selectedProject.isSnapshotSidePanelOpen);

/**
 * @returns The snapshot entity with the specified id, or null if it does not exist in the store
 * @param snapshotId - The ID of the snapshot to select
 */
export function getSnapshotByIdSelector(snapshotId) {
  return createSelector(state => selectedProjectSnapshotsSelector(state), snapshotsState => {
    return snapshotsState.find(snapshot => snapshot.id === snapshotId) ?? null;
  });
}

/**
 * Returns the project members
 */
export const projectMembersSelector = createSelector(selectedProjectSelector, selectedProject => {
  return selectedProject?.members ?? [];
});

/**
 * Returns the members of the selected project filtered by the search text
 */
export const filteredProjectMembersSelector = createSelector(state => state.ui.search.debouncedSearchText, projectMembersSelector, (searchText, members) => members.filter(member => isMemberMatchingSearch(member, searchText)));

/**
 * Get project member with the provided role
 */
export function projectMembersByRoleSelector(userRole) {
  return createSelector(projectMembersSelector, selectedMembers => {
    return selectedMembers.filter(({
      role
    }) => role === userRole);
  });
}

/**
 * Get project member with the provided role
 */
export function isFeatureEnabledForProjectSelector(projectId, feature) {
  return createSelector(getProjectByIdSelector(projectId), project => {
    if (!project || project.features === null) {
      return false;
    }
    return isFeatureEnabled(project, feature);
  });
}

/**
 * Returns the archiving state of a project
 */
export function projectArchivingStateSelector(projectId) {
  return createSelector(getProjectByIdSelector(projectId), project => {
    if (!project) {
      return null;
    }
    return project.archivingState === "UNARCHIVED" ? UtilsTypes.ProjectArchivingState.active : UtilsTypes.ProjectArchivingState.archived;
  });
}

/**
 * Returns whether the project is active or not
 */
export function isProjectActiveSelector(projectId) {
  return createSelector(projectArchivingStateSelector(projectId), archivingState => {
    return archivingState === UtilsTypes.ProjectArchivingState.active;
  });
}

/**
 * Returns whether the project is archived or not
 */
export function isProjectArchiveSelector(projectId) {
  return createSelector(projectArchivingStateSelector(projectId), archivingState => {
    return archivingState === UtilsTypes.ProjectArchivingState.archived;
  });
}

/**
 * Returns true if selected project is editable
 * Projects can be editable if: User is allowed to edit them and they are active
 */
export function isSelectedProjectEditableSelector() {
  return createSelector(selectedProjectSelector, selectedProject => {
    if (selectedProject) {
      const hasUserPermissionToEditProject = hasUserValidPermissionProjectLevel({
        permissionName: RequiredPermissionProjectLevelName.canEditProjectDetails,
        selectedProject
      });
      const isProjectActive = getProjectArchivingState(selectedProject.archivingState) === UtilsTypes.ProjectArchivingState.active;
      const isDemoProject = isProjectDemo(selectedProject);
      return hasUserPermissionToEditProject && isProjectActive && !isDemoProject;
    }
    return false;
  });
}

/**
 * Gets the project manager and its name for the selected project.
 *
 * @returns An object including the project manager of the project,
 * and the prettified version of the name.
 */
export const selectedProjectManagerSelector = createSelector(selectedProjectSelector, selectedProject => {
  const user = selectedProject ? getProjectManager(selectedProject) : null;
  const name = selectedProject ? getProjectManagerName(selectedProject) : "";
  return {
    user,
    name
  };
});

/**
 * Returns the project context of the selected project.
 */
export const selectedProjectContextSelector = createSelector(state => state.projects.selectedProject, selectedProject => {
  return selectedProject.context;
});

/**
 * Returns the settings of the selected project.
 */
export const selectedProjectSettingsSelector = createSelector(state => state.projects.selectedProject, selectedProject => {
  return selectedProject.settings;
});

/**
 * @returns whether the annotations of the current project should be displayed for viewers
 */
export const shouldDisplayMarkupsForViewersSelector = createSelector(selectedProjectSettingsSelector, settings => {
  return settings?.shouldDisplayMarkupsForViewers ?? false;
});

/**
 * @returns the project list for the given list type
 * @param listType Type of the list of projects
 */
export function projectsListSelector(listType) {
  return createSelector(state => state.projects.entities, state => state.projects.lists, (allProjectsMap, lists) => {
    const list = lists[listType];
    const projects = list.ids.map(id => allProjectsMap[id]).filter(Boolean);
    return {
      projects,
      ids: list.ids,
      nextPageCursor: list.nextPageCursor,
      hasFetchedFirstPage: list.hasFetchedFirstPage,
      totalCount: list.totalCount
    };
  });
}